import { useQuery } from "@tanstack/react-query";
import { Alert, Button, Card, Col, Progress, Row, Skeleton, Table, Tag, Typography } from "antd";
import { useNavigate } from "react-router";
import { api, unwrap } from "@/api/client";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n } from "@/i18n";
import { formatBytes } from "@/lib/bytes";

type Storage = NonNullable<Awaited<ReturnType<typeof fetchStorage>>>;
type Kind = Storage["kinds"][number];
type WarehouseUse = Storage["warehouses"][number];

const fetchStorage = () => unwrap(api.GET("/v1/workspace/storage"));

/**
 * Penyimpanan cloud (Plan §6.17): the files Zerpid keeps for this workspace,
 * by kind and, for packing videos, by warehouse, against the storage quota.
 * Clips the recorder agent keeps on packing laptops are shown apart: they
 * cost no cloud storage.
 */
export function StoragePage() {
  const { t, locale, formatNumber } = useI18n();
  const { can } = useAuth();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ["storage"], queryFn: fetchStorage });
  const s = q.data;
  if (!s) return <Skeleton active />;
  const size = (n: number) => formatBytes(n, locale === "id" ? "id-ID" : "en-GB");
  const pct = s.limitBytes ? Math.min(100, Math.round((s.usedBytes / Math.max(s.limitBytes, 1)) * 100)) : 0;
  const browserCloud = s.warehouses.some((w) => w.recorder === "browser" && w.recordPacking);

  return (
    <>
      <Typography.Title level={2} style={{ marginTop: 0, fontSize: 28 }}>
        {t("storage.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ maxWidth: 760 }}>
        {t("storage.help")}
      </Typography.Paragraph>

      {s.limitBytes !== null && pct >= 100 && <Alert type="error" showIcon style={{ marginBottom: 16 }} title={t("storage.full")} />}
      {s.limitBytes !== null && pct >= 80 && pct < 100 && <Alert type="warning" showIcon style={{ marginBottom: 16 }} title={t("storage.warn", { pct })} />}

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={9}>
          <Card title={t("storage.used")}>
            <Typography.Text className="z-num" style={{ fontSize: 28, fontWeight: 700 }}>
              {size(s.usedBytes)}
            </Typography.Text>
            <div>
              <Typography.Text type="secondary">
                {s.limitBytes === null ? t("storage.unlimited") : t("storage.of", { limit: size(s.limitBytes) })}
              </Typography.Text>
            </div>
            {s.limitBytes !== null && (
              <Progress percent={pct} status={pct >= 100 ? "exception" : "normal"} style={{ marginTop: 8 }} format={(p) => `${p}%`} />
            )}
            {s.local.files > 0 && (
              <Typography.Paragraph type="secondary" style={{ marginTop: 16, marginBottom: 0, fontSize: 13 }}>
                {t("storage.localNote", { size: size(s.local.bytes), n: formatNumber(s.local.files) })}
              </Typography.Paragraph>
            )}
          </Card>
        </Col>
        <Col xs={24} lg={15}>
          <Card title={t("storage.byKind")}>
            <Table<Kind>
              size="small"
              rowKey="kind"
              pagination={false}
              dataSource={s.kinds}
              columns={[
                { title: t("storage.kind"), dataIndex: "kind", render: (v: Kind["kind"]) => t(`storage.kind.${v}`) },
                { title: t("storage.files"), dataIndex: "files", align: "right", width: 100, render: (v: number) => <span className="z-num">{formatNumber(v)}</span> },
                { title: t("storage.size"), dataIndex: "bytes", align: "right", width: 120, render: (v: number) => <span className="z-num">{size(v)}</span> },
                {
                  title: t("storage.share"),
                  dataIndex: "bytes",
                  key: "share",
                  width: 160,
                  render: (v: number) => <Progress percent={s.usedBytes ? Math.round((v / s.usedBytes) * 100) : 0} size="small" />,
                },
              ]}
            />
          </Card>
        </Col>
      </Row>

      <Card
        title={t("storage.byWarehouse")}
        style={{ marginTop: 16 }}
        extra={
          can("settings.warehouses") && (
            <Button onClick={() => navigate("/app/settings/warehouses")}>{t("storage.settings")}</Button>
          )
        }
      >
        {browserCloud && <Alert type="info" showIcon style={{ marginBottom: 16 }} title={t("storage.agentTip")} />}
        <Table<WarehouseUse>
          size="small"
          rowKey="warehouseId"
          pagination={false}
          dataSource={s.warehouses}
          scroll={{ x: 760 }}
          columns={[
            { title: t("orders.warehouse"), dataIndex: "name" },
            {
              title: t("storage.recorder"),
              dataIndex: "recorder",
              width: 190,
              render: (v: WarehouseUse["recorder"], w) =>
                !w.recordPacking ? (
                  <Tag>{t("storage.recorder.off")}</Tag>
                ) : (
                  <Tag color={v === "agent" ? "green" : "blue"}>{t(`storage.recorder.${v}`)}</Tag>
                ),
            },
            {
              title: t("storage.retention"),
              dataIndex: "retentionDays",
              width: 120,
              render: (v: number) => <span className="z-num">{t("storage.retentionDays", { n: v })}</span>,
            },
            {
              title: t("storage.cloud"),
              dataIndex: "cloudBytes",
              width: 170,
              align: "right",
              render: (v: number, w) => <span className="z-num">{t("storage.sizeFiles", { size: size(v), n: formatNumber(w.cloudFiles) })}</span>,
            },
            {
              title: t("storage.local"),
              dataIndex: "localBytes",
              width: 170,
              align: "right",
              render: (v: number, w) => (
                <Typography.Text type="secondary" className="z-num">
                  {w.localFiles > 0 ? t("storage.sizeFiles", { size: size(v), n: formatNumber(w.localFiles) }) : "—"}
                </Typography.Text>
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
